import { forwardRef } from 'react';
import type { ChangeEvent } from 'react';

import { cn } from '@/shared/utils/cn';

import { SelectionBurst, useSelectionBurst } from './SelectionBurst';

type CheckboxProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label: React.ReactNode;
  hasError?: boolean;
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hasError, className, id, onChange, ...props },
  ref,
) {
  const { burstKey, variant, triggerBurst } = useSelectionBurst();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    triggerBurst();
    onChange?.(event);
  };

  return (
    <label
      htmlFor={id}
      className={cn(
        'flex cursor-pointer items-start gap-3 text-adipa-text-primary',
        props.disabled && 'cursor-not-allowed opacity-70',
        className,
      )}
    >
      <span className="relative mt-0.5 flex h-4 w-4 shrink-0">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          aria-invalid={hasError ? 'true' : undefined}
          className={cn(
            'peer h-4 w-4 cursor-pointer appearance-none rounded-adipa-sm border transition-colors duration-adipa-fast',
            'border-adipa-border bg-adipa-surface',
            'checked:border-adipa-primary checked:bg-adipa-primary',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-adipa-focus-ring',
            hasError && 'border-adipa-error focus-visible:ring-adipa-error-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
          onChange={handleChange}
          {...props}
        />
        <SelectionBurst burstKey={burstKey} variant={variant} className="z-20" />
        <svg
          className="pointer-events-none absolute inset-0 z-10 hidden h-4 w-4 text-adipa-primary-foreground peer-checked:block"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden
        >
          <path
            d="m3.5 8 3 3 6-6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="text-sm leading-relaxed">{label}</span>
    </label>
  );
});
